import React, { useState, useEffect } from 'react';
import { User } from '../../types/auth';
import { Business, UserTierInfo } from '../../types/accounting';
import { accountingApi } from '../../services/accountingApi';
import BusinessDashboard from './dashboard/BusinessDashboard';
import BusinessSelector from './business/BusinessSelector';
import BusinessSetup from './business/BusinessSetup';
import ErrorBoundary from '../ErrorBoundary';
import './AccountingApp.css';
import { TylerState } from '../../utils/TylerState';

interface AccountingAppProps {
  user: User | null;
}

const AccountingApp: React.FC<AccountingAppProps> = ({ user }) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [userTier, setUserTier] = useState<UserTierInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBusinessSetup, setShowBusinessSetup] = useState(false);
  const [removingSample, setRemovingSample] = useState(false);
  const activeBusinessKey = `accounting_active_business_${user?.email ?? 'guest'}`;

  // Open a business and remember it at once, so even a refresh right after a switch comes back to it
  const openBusiness = (business: Business | null) => {
    setSelectedBusiness(business);
    if (business) localStorage.setItem(activeBusinessKey, String(business.id));
  };

  // The red site nav stays on screen too, so the accounting bar sticks right under it whatever its height is
  useEffect(() => {
    const siteNav = document.querySelector<HTMLElement>('.top-navigation');
    if (!siteNav) return;
    const setNavHeight = () => document.documentElement.style.setProperty('--site-nav-height', `${siteNav.offsetHeight}px`);
    setNavHeight();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(setNavHeight);
    observer.observe(siteNav);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Simple tier setup like car calculator - map user tiers to accounting tiers
      const mapUserTierToAccounting = (tier: string | undefined) => {
        switch (tier) {
          case 'supporter': return 'supporters_club';
          case 'free': return 'regular';
          default: return 'guest';
        }
      };

      const simpleUserTier: UserTierInfo = {
        user_tier: mapUserTierToAccounting(user?.tier),
        limits: {
          max_businesses: user?.tier === 'supporter' ? 10 : 1,
          max_journal_entries: user?.tier === 'supporter' ? 10000 : 500,
          max_users_per_business: user?.tier === 'supporter' ? 10 : 1,
          can_save_data: true,
          can_export_reports: user?.tier === 'supporter',
          can_invite_users: user?.tier === 'supporter'
        },
        usage: {
          business_count: 0,
          journal_entry_count: 0
        }
      };
      setUserTier(simpleUserTier);
      
      // Load user's businesses
      const businessesResponse = await accountingApi.getBusinesses();
      const loadedBusinesses: Business[] = businessesResponse.data.businesses;
      setBusinesses(loadedBusinesses);

      // Reopen the business used last time in this browser, else the first one
      if (loadedBusinesses.length > 0) {
        const rememberedId = Number(localStorage.getItem(activeBusinessKey));
        openBusiness(loadedBusinesses.find(b => b.id === rememberedId) || loadedBusinesses[0]);
      } else {
        setShowBusinessSetup(true);
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessCreated = (business: Business) => {
    // The sample endpoint returns the existing copy if the user already has one
    setBusinesses(prev => prev.some(b => b.id === business.id) ? prev : [...prev, business]);
    openBusiness(business);
    setShowBusinessSetup(false);
  };

  const handleRemoveSample = async () => {
    if (!selectedBusiness?.is_sample) return;
    if (!window.confirm(`Remove ${selectedBusiness.business_name} and everything in it? You can explore it again any time.`)) return;
    try {
      setRemovingSample(true);
      await accountingApi.deleteBusiness(selectedBusiness.id);
      const remaining = businesses.filter(b => b.id !== selectedBusiness.id);
      setBusinesses(remaining);
      openBusiness(remaining[0] || null);
      if (remaining.length === 0) setShowBusinessSetup(true);
    } catch (err: any) {
      alert(err.message || 'Failed to remove the sample business');
    } finally {
      setRemovingSample(false);
    }
  };

  if (!user) {
    // Demo business data for logged-out users
    const demoYear = new Date().getFullYear();
    const daysAgo = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return d.toISOString().split('T')[0];
    };
    const demoBusiness = {
      id: 0,
      business_name: "Demo Restaurant Ltd.",
      business_type: "corporation",
      currency: "CAD",
      fiscal_year_end: `${demoYear}-12-31`
    };

    const demoDashboardData = {
      balance_summary: {
        total_assets: 125750.00,
        total_liabilities: 32500.00,
        total_equity: 93250.00,
        balance_check: true
      },
      account_summary: {
        asset: 8,
        liability: 4,
        equity: 3,
        revenue: 6,
        expense: 12
      },
      recent_entries: [
        {
          id: 1,
          entry_number: "JE-0034",
          entry_date: daysAgo(4),
          description: "Monthly rent payment",
          total_amount: 2500.00,
          status: "posted"
        },
        {
          id: 2,
          entry_number: "JE-0033",
          entry_date: daysAgo(5),
          description: "Food supplier invoice",
          total_amount: 1875.50,
          status: "posted"
        },
        {
          id: 3,
          entry_number: "JE-0032",
          entry_date: daysAgo(6),
          description: "Daily sales deposit",
          total_amount: 3420.00,
          status: "posted"
        }
      ]
    };

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: demoBusiness.currency
      }).format(amount);
    };

    return (
      <div className="accounting-app-container">
        <header className="accounting-header">
          <div className="header-content">
            <h1>📊 Larry's Help Accounting</h1>
              <button 
                className="btn-primary"
                onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
              >
                🔐 Log In to Get Started
              </button>
          </div>
        </header>

        <main className="accounting-main">
          <div className="business-dashboard">
            <header className="dashboard-header">
              <div className="business-info">
                <div className="business-meta">
                  <span className="business-type">{demoBusiness.business_type}</span>
                  <span className="fiscal-year">FY ends {demoBusiness.fiscal_year_end}</span>
                </div>
              </div>
              <div className="dashboard-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  📊 Reports
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  ➕ New Entry
                </button>
              </div>
            </header>

            <div className="dashboard-content">
              {/* Account Summary */}
              <section style={{marginBottom: '3rem'}}>
                <h3 style={{marginBottom: '1.5rem', color: '#1f2937', fontSize: '1.25rem'}}>📁 Chart of Accounts</h3>
                <div className="account-types">
                  {Object.entries(demoDashboardData.account_summary).map(([type, count]) => (
                    <div key={type} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{
                        fontSize: '1.5rem',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8fafc',
                        borderRadius: '8px'
                      }}>
                        {type === 'asset' && '💰'}
                        {type === 'liability' && '📋'}
                        {type === 'equity' && '🏦'}
                        {type === 'revenue' && '📈'}
                        {type === 'expense' && '💸'}
                      </div>
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 500, color: '#1f2937', marginBottom: '0.25rem'}}>
                          {({asset: 'Assets', liability: 'Liabilities', equity: 'Equity',
                             revenue: 'Revenue', expense: 'Expenses'} as Record<string, string>)[type] || type}
                        </div>
                        <div style={{fontSize: '0.875rem', color: '#6b7280'}}>
                          {count} accounts
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Recent Journal Entries */}
              <section className="recent-scenarios">
                <h2>📝 Recent Journal Entries</h2>
                <div className="scenario-cards">
                  {demoDashboardData.recent_entries.map(entry => (
                    <div key={entry.id} className="scenario-card">
                      <div className="scenario-type">{entry.entry_number}</div>
                      <div className="scenario-time">{entry.entry_date}</div>
                      <div style={{
                        fontSize: '1rem',
                        marginBottom: '1rem',
                        fontWeight: '500'
                      }}>
                        {entry.description}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '1rem'
                      }}>
                        <span style={{
                          background: 'var(--white-20)',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {entry.status}
                        </span>
                        <span style={{
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#059669'
                        }}>
                          {formatCurrency(entry.total_amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Quick Actions - using existing action-card classes */}
            <section className="quick-actions">
              <h3 style={{marginBottom: '1.5rem', color: '#1f2937', fontSize: '1.25rem'}}>⚡ Quick Actions</h3>
              <div className="action-cards">
                <button 
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>➕</span>
                  <div>New Journal Entry</div>
                </button>

                <button 
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>📁</span>
                  <div>Chart of Accounts</div>
                </button>

                <button 
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>📊</span>
                  <div>Trial Balance</div>
                </button>

                <button 
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>📄</span>
                  <div>Balance Sheet</div>
                </button>

                <button
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>🏦</span>
                  <div>Bank Import</div>
                </button>

                <button
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>👥</span>
                  <div>Invite Users</div>
                </button>

                <button 
                  className="action-card"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  <span className="card-icon" style={{color: '#dc2626'}}>⚙️</span>
                  <div>Business Settings</div>
                </button>
              </div>
            </section>

            {/* Call to Action */}
            <section className="demo-cta">
              <div className="cta-content">
                <h3>See it with real books</h3>
                <p>
                  Create a free account and you get your own copy of Maple Ridge Renovations, a small
                  contractor with three months of books. Change anything.
                </p>
                <div className="cta-features">
                  <div className="feature">✅ Double-entry bookkeeping</div>
                  <div className="feature">✅ Financial reports & trial balance</div>
                  <div className="feature">✅ Multi-business support</div>
                  <div className="feature">✅ Complete audit trail</div>
                </div>
                <button 
                  className="btn-primary cta-button"
                  onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
                >
                  Get Started Free
                </button>
              </div>
            </section>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="accounting-app-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your accounting data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accounting-app-container">
        <div className="error-state">
          <h3>⚠️ Error</h3>
          <p>{error}</p>
          <button className="btn-secondary" onClick={loadUserData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if user needs upgrade
  if (userTier && !userTier.limits.can_save_data) {
    return (
      <div className="accounting-app-container">
        <div className="upgrade-required">
          <h2>Ⓛ Larry's Help Accounting</h2>
          <div className="upgrade-prompt">
            <h3>🔓 Upgrade Required</h3>
            <p>
              The accounting system requires a Regular account or higher. 
              Create your free account to get started!
            </p>
            <div className="tier-benefits">
              <h4>With a Regular Account:</h4>
              <ul>
                <li>✅ Create up to 3 businesses</li>
                <li>✅ Record up to 500 journal entries per month</li>
                <li>✅ Generate financial reports</li>
                <li>✅ Save your data</li>
              </ul>
            </div>
            <button 
              className="btn-primary"
              onClick={() => window.location.href = '/account'}
            >
              Upgrade Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accounting-app-container">
      <header className="accounting-header">
        <div className="header-content">
          <h1>Ⓛ Larry's Help Accounting</h1>
          <div className="header-actions">
            <BusinessSelector
              businesses={businesses}
              selectedBusiness={selectedBusiness}
              onSelectBusiness={openBusiness}
              onCreateNew={() => setShowBusinessSetup(true)}
              userTier={userTier}
            />
            {userTier && (
              <div style={{"marginTop":"0px","height":"100%"}}>
                <span className={`acct-tier-badge acct-tier-${userTier.user_tier}`}>
                  {userTier.user_tier.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {TylerState.user && (
      <main className="accounting-main">
        {showBusinessSetup ? (
          <ErrorBoundary context="business_setup">
            <BusinessSetup
              user={TylerState.user}
              userTier={userTier}
              hasSample={businesses.some(b => b.is_sample)}
              canCancel={businesses.length > 0}
              onBusinessCreated={handleBusinessCreated}
              onCancel={() => setShowBusinessSetup(false)}
            />
          </ErrorBoundary>
        ) : selectedBusiness ? (
          <ErrorBoundary context="business_dashboard">
            {selectedBusiness.is_sample && (
              <div className="sample-banner" role="status">
                <span className="sample-badge">Sample</span>
                <span>
                  You're exploring <strong>{selectedBusiness.business_name}</strong>. Change anything:
                  nothing here touches a real business.
                </span>
                <div className="sample-banner-actions">
                  <button className="btn-primary" onClick={() => setShowBusinessSetup(true)}>
                    Create My Business
                  </button>
                  <button className="btn-secondary" onClick={handleRemoveSample} disabled={removingSample}>
                    {removingSample ? 'Removing...' : 'Remove Sample'}
                  </button>
                </div>
              </div>
            )}
            <BusinessDashboard
              key={selectedBusiness.id}
              business={selectedBusiness}
              user={TylerState.user}
              userTier={userTier}
            />
          </ErrorBoundary>
        ) : (
          <div className="no-business-state">
            <h3>🏢 No Businesses Found</h3>
            <p>Create your first business to get started with bookkeeping.</p>
            <button 
              className="btn-primary"
              onClick={() => setShowBusinessSetup(true)}
            >
              Create Business
            </button>
          </div>
        )}
      </main>
      )}
    </div>
  );
};

export default AccountingApp;